import GradientBorder from "../GradientBorder";

import { motion } from "framer-motion";
import React, { useState } from "react";
import { MdDelete } from "react-icons/md";
import DateIcon from "src/assets/icons/calendar.svg";
import DownloadIcon from "src/assets/icons/download.svg";
import DuplicateIcon from "src/assets/icons/duplicate-2.svg";
import PriceIcon from "src/assets/icons/empty-wallet.svg";
import SuitCaseIcon from "src/assets/icons/suitcase.svg";
import LocationIcon from "src/assets/icons/map.svg";
import ShareIcon from "src/assets/icons/share.svg";
import IconButton from "src/components/Buttons/IconButton";
import IconWithText from "src/components/Buttons/IconWithText";
import LabelButton from "src/components/Buttons/LabelButton";
import { ItineraryItemSchema, TripItemType } from "src/utils/ApiSchema";

interface Props {
  itinerary: ItineraryItemSchema;
  type: TripItemType;
  onClick?: () => void;
  className?: string;
}

function ItineraryItemView(props: Props) {
  const [hover, setHover] = useState(false);

  const itinerary = props.itinerary;
  const onDelete = () => {
    if (window.confirm("Are you sure you want to delete?")) {
      console.log("User clicked OK");
    } else {
      console.log("User clicked Cancel");
    }
  };

  return (
    <div
      id={`itinerary-item-container-${itinerary.id}`} // tracking ID on outer container
      className="relative w-full flex mt-[12px] p-[2px]"
    >
      <GradientBorder
        hover={hover}
        type={props.type}
        completed={itinerary.completed}
      />
      <motion.div
        id={`itinerary-item-${itinerary.id}`} // tracking ID for main item container
        className={`flex flex-col w-full justify-center rounded-[22px]  ${
          itinerary.completed
            ? "bg-gray-500"
            : props.type === TripItemType.Background
            ? "bg-white"
            : ""
        } ${props.className}`}
        onClick={props.onClick}
        style={{
          backdropFilter: "blur(19.5px)",
          boxShadow: "0px 4px 162.7px 0px rgba(255, 255, 255, 0.12) inset",
        }}
        whileHover="hover"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <img
          src={SuitCaseIcon}
          className={`absolute -z-10 h-full object-cover right-[16px] opacity-60`}
          alt="Suitcase icon"
        />
        <div className="flex items-center justify-between min-w-screen px-[16px] pt-[16px]">
          <p
            className={`flex ${
              props.type === TripItemType.Background ? "text-black" : "text-white"
            } font-bold text-m`}
          >
            {itinerary.name}
          </p>
          <div className="flex">
            <LabelButton
              label={itinerary.completed ? "Completed" : "Complete"}
              className={itinerary.completed ? "bg-black" : "bg-white"}
              labelClassName={itinerary.completed ? "text-white" : "invert"}
            />
            <button
              id={`itinerary-delete-btn-${itinerary.id}`} // tracking ID for delete button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <MdDelete
                size={24}
                className={`ml-[16px] ${
                  props.type === TripItemType.Background
                    ? "text-black"
                    : "text-white dark:text-white"
                }`}
              />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between mt-[16px] pl-[16px] pb-[16px]">
          <div className="flex flex-col">
            <IconWithText
              type={props.type}
              icon={LocationIcon}
              label={itinerary.location}
            />
            <IconWithText
              type={props.type}
              icon={DateIcon}
              label={new Intl.DateTimeFormat("en-US", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              }).format(new Date(itinerary.time))}
            />
            <IconWithText
              type={props.type}
              icon={PriceIcon}
              label={itinerary.cost.currency + " " + itinerary.cost.totalValue}
            />
          </div>
          <div className="flex h-full items-end justify-end pr-[16px]">
            <div
              className={`flex items-end justify-end ${
                props.type === TripItemType.Background
                  ? "bg-white border-[1px] border-gray-100"
                  : "priceBackground drop-shadow-xl shadow-black "
              }  p-[8px] rounded-full`}
            >
              <IconButton icon={DownloadIcon} type={props.type} />
              <IconButton icon={ShareIcon} type={props.type} />
              <IconButton
                icon={DuplicateIcon}
                type={props.type}
                className={
                  props.type === TripItemType.Background
                    ? "invert-0 dark:invert"
                    : "invert dark:invert-0"
                }
              />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default ItineraryItemView;
